import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown, ArrowLeft, ArrowUp, Camera, Check, CircleAlert, Download,
  ImagePlus, Loader2, Plus, Save, Trash2, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { InvoiceApi } from "@/lib/invoice-api";
import DeliveryNoteImport from "@/components/project/delivery-note-import";
import InvoiceStartPage from "@/components/project/invoice-start-page";
import InvoicePreview from "@/components/project/invoice-preview";
import InvoiceLayoutEditor from "@/components/project/invoice-layout-editor";
import { useInvoiceLayoutCheck } from "@/hooks/use-invoice-layout-check";
import { applyDeliveryNote } from "@/lib/delivery-note-items";
import { generateInvoicePDF } from "@/lib/invoice-pdf";
import { DEFAULT_INVOICE_COVER_PAGE, DEFAULT_INVOICE_PART_TITLES, INVOICE_PARTS, calculateInvoiceTotals, type InvoiceData, type InvoiceItem, type InvoicePhotoMetadata, type InvoiceRecord } from "@shared/invoice";

const money = new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" });
const dateLabel = (value: string | Date | null) => value ? new Date(value).toLocaleDateString("de-AT") : "—";
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const freshItem = (): InvoiceItem => ({
  id: `position-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  description: "", quantity: 1, unit: "Stk.", unitPrice: 0, billable: true, teil: "A",
});
const formatBytes = (bytes: number) => bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return <div className="space-y-1.5"><label className="block space-y-1.5"><span className="block text-sm font-medium text-slate-700">{label}</span>{children}</label>{hint && <p className="text-xs leading-relaxed text-slate-500">{hint}</p>}</div>;
}

function moneyInput(value: number, onChange: (value: number) => void) {
  return <Input type="number" min="0" step="0.01" inputMode="decimal" value={Number.isFinite(value) ? value : 0}
    onChange={(event) => onChange(Math.max(0, Number(event.target.value) || 0))} className="tabular-nums" />;
}

export default function InvoiceEditPage() {
  const [, params] = useRoute("/invoice/:id");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const id = params?.id;
  const [data, setData] = useState<InvoiceData | null>(null);
  const layoutCheck = useInvoiceLayoutCheck(data);
  const [baseline, setBaseline] = useState("");
  const [photos, setPhotos] = useState<InvoicePhotoMetadata[]>([]);
  const [saveError, setSaveError] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [busy, setBusy] = useState(false);
  const [deliveryBusy, setDeliveryBusy] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [loadingPaymentDefaults, setLoadingPaymentDefaults] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [photoSaving, setPhotoSaving] = useState<number | null>(null);
  const [photoDrafts, setPhotoDrafts] = useState<Record<number, { displayName: string; description: string }>>({});
  const fileRef = useRef<HTMLInputElement>(null);
  const currentDataRef = useRef<InvoiceData | null>(data);
  const initializedId = useRef<string>();
  currentDataRef.current = data;

  const invoiceQuery = useQuery<InvoiceRecord>({
    queryKey: ["invoice", id],
    queryFn: () => InvoiceApi.get(id!),
    enabled: !!id,
  });
  const photoQuery = useQuery<InvoicePhotoMetadata[]>({
    queryKey: ["invoice-photos", id],
    queryFn: () => InvoiceApi.photos(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (!invoiceQuery.data || initializedId.current === id) return;
    initializedId.current = id;
    const initial = clone(invoiceQuery.data.data);
    setData(initial);
    setBaseline(JSON.stringify(initial));
  }, [invoiceQuery.data]);
  useEffect(() => {
    if (photoQuery.data) {
      setPhotos(photoQuery.data);
      setPhotoDrafts((current) => Object.fromEntries(photoQuery.data.map((photo) => [photo.id, current[photo.id] ?? {
        displayName: photo.displayName, description: photo.description,
      }])));
    }
  }, [photoQuery.data]);

  const photoDirty = photos.some((photo) => {
    const draft = photoDrafts[photo.id];
    return draft && (draft.displayName !== photo.displayName || draft.description !== photo.description);
  });
  const dirty = (!!data && JSON.stringify(data) !== baseline) || photoDirty;
  useEffect(() => {
    if (!dirty && !deliveryBusy) return;
    const prevent = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", prevent);
    return () => window.removeEventListener("beforeunload", prevent);
  }, [dirty, deliveryBusy]);

  const totals = useMemo(() => data ? calculateInvoiceTotals(data) : { subtotal: 0, tax: 0, total: 0 }, [data]);
  const previewPhotos = useMemo(() => photos.map((photo) => ({
    ...photo, ...(photoDrafts[photo.id] ?? {}),
  })), [photos, photoDrafts]);
  const setField = <K extends keyof InvoiceData>(key: K, value: InvoiceData[K]) =>
    setData((current) => current ? { ...current, [key]: value } : current);
  const updateItem = (itemId: string, patch: Partial<InvoiceItem>) =>
    setData((current) => current ? { ...current, items: current.items.map((item) => item.id === itemId ? { ...item, ...patch } : item) } : current);
  const moveItem = (index: number, direction: -1 | 1) => setData((current) => {
    if (!current) return current;
    const next = [...current.items];
    const target = index + direction;
    if (target < 0 || target >= next.length) return current;
    [next[index], next[target]] = [next[target], next[index]];
    return { ...current, items: next };
  });

  const save = useCallback(async (silent = false) => {
    const current = currentDataRef.current;
    if (!id || !current) return false;
    setSaveState("saving");
    setSaveError("");
    try {
      let nextPhotos = photos;
      for (const photo of photos) {
        const draft = photoDrafts[photo.id];
        if (!draft || (draft.displayName === photo.displayName && draft.description === photo.description)) continue;
        const updated = await InvoiceApi.updatePhoto(id, photo.id, draft);
        nextPhotos = nextPhotos.map((entry) => entry.id === photo.id ? updated : entry);
        setPhotos(nextPhotos);
        queryClient.setQueryData(["invoice-photos", id], nextPhotos);
      }
      const saved = await InvoiceApi.save(id, current);
      const canonical = clone(saved.data);
      currentDataRef.current = canonical;
      setData(canonical);
      setBaseline(JSON.stringify(canonical));
      queryClient.setQueryData(["invoice", id], saved);
      setSaveState("saved");
      if (!silent) toast({ title: "Entwurf gespeichert", description: "Ihre Änderungen sind gesichert." });
      window.setTimeout(() => setSaveState((state) => state === "saved" ? "idle" : state), 2200);
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Speichern fehlgeschlagen.";
      setSaveError(message);
      setSaveState("idle");
      return false;
    }
  }, [id, queryClient, toast, photos, photoDrafts]);

  const leaveTo = (path: string) => {
    if (deliveryBusy && !window.confirm("Ein Lieferschein wird noch erkannt. Seite trotzdem verlassen?")) return;
    if (dirty && !window.confirm("Es gibt ungespeicherte Änderungen. Seite trotzdem verlassen?")) return;
    navigate(path);
  };

  const applyPaymentDefaults = async () => {
    setLoadingPaymentDefaults(true);
    try {
      const defaults = await InvoiceApi.defaults();
      if (!defaults.paymentQr?.recipient.trim() || !defaults.paymentQr.iban.trim()) {
        toast({ title: "Noch keine Bankdaten gespeichert", description: "Bitte Kontoinhaber und IBAN einmal eintragen und die Rechnung speichern." });
        return;
      }
      setData((current) => current ? { ...current, coverPage: {
        ...DEFAULT_INVOICE_COVER_PAGE, ...current.coverPage,
        paymentQr: { ...defaults.paymentQr!, enabled: true },
      } } : current);
      toast({ title: "Bankdaten übernommen", description: "Sie können die Angaben noch ändern. Zum Übernehmen in diese Rechnung bitte speichern." });
    } catch (error) {
      toast({ title: "Bankdaten nicht geladen", description: error instanceof Error ? error.message : "Bitte erneut versuchen.", variant: "destructive" });
    } finally {
      setLoadingPaymentDefaults(false);
    }
  };

  const saveDefaults = async () => {
    const current = currentDataRef.current;
    if (!current) return;
    setSavingDefaults(true);
    try {
      await InvoiceApi.saveDefaults({
        sellerUid: current.sellerUid,
        sellerDgNumber: current.coverPage?.sellerDgNumber ?? "",
        additionalCompanyInfo: current.coverPage?.additionalCompanyInfo ?? "",
        paymentQr: current.coverPage?.paymentQr,
      });
      toast({ title: "Firmendaten gespeichert", description: "Firmendaten und Zahlungseinstellungen werden für neue Rechnungen übernommen." });
    } catch (error) {
      toast({ variant: "destructive", title: "Vorgaben nicht gespeichert", description: error instanceof Error ? error.message : "Bitte erneut versuchen." });
    } finally { setSavingDefaults(false); }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!files.length || !id) return;
    setBusy(true);
    setPhotoError("");
    try {
      let next = [...photos];
      const failures: string[] = [];
      for (const file of files) {
        try {
          const photo = await InvoiceApi.uploadPhoto(id, file);
          next = [...next, photo];
          setPhotos(next);
          queryClient.setQueryData(["invoice-photos", id], next);
        } catch (error) {
          failures.push(`${file.name}: ${error instanceof Error ? error.message : "Upload fehlgeschlagen"}`);
        }
      }
      if (failures.length) setPhotoError(failures.join(" · "));
      if (next.length > photos.length) toast({ title: "Fotos hinzugefügt", description: `${next.length - photos.length} Foto(s) wurden in dieser Rechnung gespeichert.` });
    } catch (error) {
      setPhotoError(error instanceof Error ? error.message : "Foto-Upload fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  const savePhoto = async (photo: InvoicePhotoMetadata) => {
    if (!id) return;
    const draft = photoDrafts[photo.id] ?? { displayName: photo.displayName, description: photo.description };
    if (!draft.displayName.trim()) { setPhotoError("Bitte vergeben Sie einen Fototitel."); return; }
    setPhotoSaving(photo.id); setPhotoError("");
    try {
      const updated = await InvoiceApi.updatePhoto(id, photo.id, { displayName: draft.displayName.trim(), description: draft.description });
      const next = photos.map((entry) => entry.id === photo.id ? updated : entry);
      setPhotos(next);
      setPhotoDrafts((current) => ({ ...current, [photo.id]: { displayName: updated.displayName, description: updated.description } }));
      queryClient.setQueryData(["invoice-photos", id], next);
      toast({ title: "Fotodetails gespeichert" });
    } catch (error) { setPhotoError(error instanceof Error ? error.message : "Fotodetails konnten nicht gespeichert werden."); }
    finally { setPhotoSaving(null); }
  };

  const removePhoto = async (photo: InvoicePhotoMetadata) => {
    if (!id || !window.confirm(`„${photo.displayName || photo.fileName}“ wirklich aus dieser Rechnung entfernen?`)) return;
    setPhotoError("");
    try {
      await InvoiceApi.deletePhoto(id, photo.id);
      const next = photos.filter((entry) => entry.id !== photo.id);
      setPhotos(next);
      setPhotoDrafts((current) => { const copy = { ...current }; delete copy[photo.id]; return copy; });
      queryClient.setQueryData(["invoice-photos", id], next);
      toast({ title: "Foto entfernt" });
    } catch (error) { setPhotoError(error instanceof Error ? error.message : "Foto konnte nicht entfernt werden."); }
  };

  const exportPdf = async () => {
    if (!data || !id || deliveryBusy) return;
    if (photoQuery.isLoading || photoQuery.isError) {
      toast({ variant: "destructive", title: "Fotos noch nicht geladen", description: "Bitte die Fotoliste zuerst vollständig laden, damit kein Rechnungsfoto im PDF fehlt." });
      return;
    }
    const errors: string[] = [];
    if (!data.invoiceNumber.trim()) errors.push("Rechnungsnummer");
    if (!data.customerName.trim() || !data.customerAddress.trim()) errors.push("Kundendaten");
    if (!data.servicePeriod.trim()) errors.push("Leistungszeitraum");
    if (!data.items.some((item) => item.billable && item.description.trim())) errors.push("mindestens eine benannte, abrechenbare Position");
    if (errors.length) {
      toast({ variant: "destructive", title: "PDF noch nicht bereit", description: `Bitte ergänzen: ${errors.join(", ")}.` });
      return;
    }
    setBusy(true);
    try {
      if (dirty && !(await save(true))) return;
      const latest = currentDataRef.current;
      if (!latest) return;
      const savedPhotos = await InvoiceApi.photos(id);
      const blob = await generateInvoicePDF(latest, savedPhotos, id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Rechnung_${latest.invoiceNumber.replace(/[^a-zA-Z0-9äöüÄÖÜ_-]/g, "_")}.pdf`;
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({ title: "PDF heruntergeladen", description: "Der Rechnungsentwurf wurde als PDF exportiert." });
    } catch (error) {
      toast({ variant: "destructive", title: "PDF-Export fehlgeschlagen", description: error instanceof Error ? error.message : "PDF konnte nicht erstellt werden." });
    } finally { setBusy(false); }
  };

  if (invoiceQuery.isLoading) return <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6"><div className="animate-pulse space-y-4" aria-label="Rechnung wird geladen"><div className="h-8 w-64 rounded bg-bauwerk-mint" /><div className="h-40 rounded-lg bg-white/70" /><div className="h-72 rounded-lg bg-white/70" /></div></main>;
  if (invoiceQuery.isError || !invoiceQuery.data || !data) return <main className="mx-auto max-w-3xl px-4 py-12"><Card><CardContent className="flex flex-col items-start gap-3 p-6"><CircleAlert className="h-6 w-6 text-destructive" /><h1 className="text-xl font-semibold">Rechnung konnte nicht geladen werden</h1><p className="text-sm text-muted-foreground">{invoiceQuery.error instanceof Error ? invoiceQuery.error.message : "Bitte versuchen Sie es erneut."}</p><Button variant="outline" onClick={() => invoiceQuery.refetch()}>Erneut laden</Button></CardContent></Card></main>;

  const projectHref = `/project/edit/${invoiceQuery.data.projectId}`;
  const changed = (key: number, field: "displayName" | "description", value: string) =>
    setPhotoDrafts((current) => ({ ...current, [key]: { ...(current[key] ?? { displayName: "", description: "" }), [field]: value } }));

  return <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
    <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} aria-label="Rechnungsfotos auswählen" />
    <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <button type="button" onClick={() => leaveTo(projectHref)} className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-bauwerk-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bauwerk-teal rounded">
          <ArrowLeft className="h-4 w-4" /> Zum Projekt
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold text-bauwerk-teal sm:text-3xl">Rechnungsentwurf</h1>
          <span className="rounded-full bg-bauwerk-mint px-2.5 py-1 text-xs font-semibold text-bauwerk-dark-teal">Rechnung #{invoiceQuery.data.id}</span>
        </div>
        <p className="mt-1 text-sm text-slate-600">{data.projectName || "Projekt"} · unabhängige Kopie, das Angebot bleibt unverändert</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`mr-1 inline-flex items-center gap-1.5 text-xs font-medium ${dirty ? "text-amber-700" : "text-slate-500"}`} aria-live="polite">
          {saveState === "saving" ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Speichert…</> : saveState === "saved" ? <><Check className="h-3.5 w-3.5" /> Gespeichert</> : dirty ? "Ungespeicherte Änderungen" : "Alle Änderungen gespeichert"}
        </span>
        <Button variant="outline" onClick={() => void exportPdf()} disabled={busy || deliveryBusy || saveState === "saving"}><Download className="mr-2 h-4 w-4" />{busy ? "Wird vorbereitet…" : "PDF herunterladen"}</Button>
        <Button onClick={() => void save()} disabled={!dirty || busy || deliveryBusy || photoSaving !== null || saveState === "saving"} className="bg-bauwerk-orange hover:bg-bauwerk-light-orange"><Save className="mr-2 h-4 w-4" />Speichern</Button>
      </div>
    </header>
    {saveError && <div role="alert" className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"><CircleAlert className="mt-0.5 h-4 w-4 shrink-0" /><div className="flex-1"><strong>Änderungen nicht gespeichert.</strong> {saveError}</div><Button variant="ghost" size="sm" disabled={deliveryBusy || busy || saveState === "saving"} onClick={() => void save()}>Erneut versuchen</Button></div>}
    <fieldset disabled={saveState === "saving" || busy || deliveryBusy || photoSaving !== null} className="grid min-w-0 grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
      <div className="min-w-0 space-y-5">
        <section aria-label="Startseite der Rechnung" className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-bauwerk-teal">Startseite / Rechnungsdeckblatt</h2>
            <p className="mt-1 text-sm text-slate-600">Angaben aus der Buchhaltung eintragen. Im PDF folgt nach dem Deckblatt die detaillierte Aufstellung mit Fotos.</p>
          </div>
          <InvoiceStartPage data={data} onChange={(patch) => setData((current) => current ? { ...current, ...patch } : current)}
            onSaveDefaults={() => void saveDefaults()} savingDefaults={savingDefaults}
            onApplyPaymentDefaults={() => void applyPaymentDefaults()} loadingPaymentDefaults={loadingPaymentDefaults} />
        </section>
        <InvoiceLayoutEditor layoutCheck={layoutCheck} layout={data.coverPage?.layout} onChange={(layout) => setField("coverPage", {
          ...DEFAULT_INVOICE_COVER_PAGE, ...data.coverPage, layout,
        })} />
        <InvoicePreview layoutCheck={layoutCheck} data={data} photos={previewPhotos} invoiceId={id!}
          photosLoading={photoQuery.isLoading || busy} photosError={photoQuery.isError} disabled={deliveryBusy} />
        <DeliveryNoteImport
          invoiceId={id!}
          notes={data.deliveryNotes ?? []}
          onChange={(notes) => setField("deliveryNotes", notes)}
          onBusyChange={setDeliveryBusy}
          onApply={(noteId) => {
            const current = currentDataRef.current;
            const note = current?.deliveryNotes?.find((entry) => entry.id === noteId);
            if (!current || !note) return;
            try {
              const result = applyDeliveryNote(note);
              if (current.items.length + result.items.length > 2000) {
                throw new Error("Die Rechnung darf höchstens 2000 Positionen enthalten.");
              }
              setData({
                ...current,
                items: [...current.items, ...result.items],
                deliveryNotes: current.deliveryNotes!.map((entry) => entry.id === noteId ? result.note : entry),
              });
              toast({ title: "Materialpositionen übernommen", description: `${result.items.length} Position(en) ergänzt. Bitte den Rechnungsentwurf speichern.` });
            } catch (error) {
              toast({ variant: "destructive", title: "Übernahme nicht möglich", description: error instanceof Error ? error.message : "Bitte die Materialzeilen prüfen." });
            }
          }}
        />
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3 pb-3">
            <div><CardTitle className="text-base text-bauwerk-teal">Abgerechnete Leistungen</CardTitle><p className="mt-1 text-xs text-slate-500">Nur eingeschaltete Positionen gehen in die Summe ein.</p></div>
            <Button variant="outline" size="sm" onClick={() => setData((current) => current ? { ...current, items: [...current.items, freshItem()] } : current)}><Plus className="mr-1.5 h-4 w-4" />Eigene Position</Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/70 p-3">
              <label className="flex cursor-pointer items-start gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" aria-label="Nach Teilen A/B/C gruppieren" checked={data.groupByPart === true}
                  onChange={(event) => setField("groupByPart", event.target.checked)} className="mt-0.5 h-4 w-4 accent-[var(--bauwerk-teal)]" />
                <span>Nach Teilen A/B/C gruppieren</span>
              </label>
              <p className="text-xs leading-relaxed text-slate-600">Die Gruppierung ändert weder Reihenfolge noch Summen. Ältere Rechnungen ohne gespeicherte Zuordnung erscheinen zunächst in Teil A; Sie können jede Position einzeln zuordnen. Nicht abrechenbare Positionen bleiben nicht abrechenbar.</p>
              {data.groupByPart && <div className="grid gap-3 sm:grid-cols-3">
                {INVOICE_PARTS.map((part) => (
                  <Field key={part} label={`Rechnungsteil ${part} – Überschrift`}>
                    <Input aria-label={`Rechnungsteil ${part} – Überschrift`}
                      value={data.partTitles?.[part] ?? DEFAULT_INVOICE_PART_TITLES[part]}
                      onChange={(event) => setField("partTitles", {
                        ...DEFAULT_INVOICE_PART_TITLES, ...data.partTitles, [part]: event.target.value,
                      })} />
                  </Field>
                ))}
              </div>}
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-900">
              Varianten aus dem Angebot wurden nicht abrechenbar übernommen. Schalten Sie sie erst ein, wenn die Leistung tatsächlich ausgeführt wurde. Diese Rechnung ist eine unabhängige Kopie; das Angebot wird nicht verändert.
            </div>
            {!data.items.length && <div className="rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">Noch keine Positionen. Fügen Sie eine tatsächlich erbrachte Leistung hinzu.</div>}
            {data.items.map((item, index) => (
              <section key={item.id} className={`rounded-lg border p-3 transition-colors sm:p-4 ${item.billable ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50/70"}`}>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-bauwerk-mint text-xs font-semibold text-bauwerk-dark-teal">{index + 1}</span>
                    <span className="truncate text-sm font-semibold text-slate-700">{item.description.trim() || "Neue Position"}</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button variant="ghost" size="icon" aria-label={`Position ${index + 1} nach oben`} disabled={index === 0} onClick={() => moveItem(index, -1)}><ArrowUp className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" aria-label={`Position ${index + 1} nach unten`} disabled={index === data.items.length - 1} onClick={() => moveItem(index, 1)}><ArrowDown className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" aria-label={`Position ${index + 1} entfernen`} onClick={() => setData((current) => current ? { ...current, items: current.items.filter((candidate) => candidate.id !== item.id) } : current)} className="text-slate-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-12">
                  <div className="col-span-2 sm:col-span-5"><Field label="Beschreibung"><Textarea rows={2} value={item.description} onChange={(e) => updateItem(item.id, { description: e.target.value })} /></Field></div>
                  <div className="col-span-1 sm:col-span-2"><Field label="Menge"><Input type="number" min="0" step="0.01" value={item.quantity} onChange={(e) => updateItem(item.id, { quantity: Math.max(0, Number(e.target.value) || 0) })} /></Field></div>
                  <div className="col-span-1 sm:col-span-2"><Field label="Einheit"><Input value={item.unit} onChange={(e) => updateItem(item.id, { unit: e.target.value })} placeholder="m²" /></Field></div>
                  <div className="col-span-1 sm:col-span-3"><Field label="EP (€)">{moneyInput(item.unitPrice, (unitPrice) => updateItem(item.id, { unitPrice }))}</Field></div>
                  <div className="col-span-2 sm:col-span-12"><Field label="Rechnungsteil"><select aria-label={`Rechnungsteil für Position ${index + 1}`} value={item.teil ?? "A"}
                    onChange={(event) => updateItem(item.id, { teil: event.target.value as InvoiceItem["teil"] })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    {INVOICE_PARTS.map((part) => <option key={part} value={part}>Teil {part}</option>)}
                  </select></Field></div>
                  <div className="col-span-2 sm:col-span-12"><Field label="Zusatztext / Herkunft (optional)" hint="Automatische Foto- oder Lieferscheinreferenz ändern oder vollständig löschen.">
                    <Textarea rows={2} value={item.sourceLabel ?? ""} onChange={(e) => updateItem(item.id, { sourceLabel: e.target.value })} />
                  </Field></div>
                  <label className="col-span-2 flex cursor-pointer items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm sm:col-span-12">
                    <input type="checkbox" aria-label={`Position ${index + 1} abrechnen`} checked={item.billable} onChange={(e) => updateItem(item.id, { billable: e.target.checked })} className="h-4 w-4 accent-[var(--bauwerk-teal)]" />
                    <span className="font-medium text-slate-700">Abrechnen</span>
                    <span className="ml-auto text-sm tabular-nums text-slate-600">{item.billable ? money.format(item.quantity * item.unitPrice) : "Nicht in Summe"}</span>
                  </label>
                </div>
              </section>
            ))}
            <Button variant="outline" className="w-full" onClick={() => setData((current) => current ? { ...current, items: [...current.items, freshItem()] } : current)}>
              <Plus className="mr-2 h-4 w-4" />Eigene Rechnungsposition hinzufügen
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-col gap-3 pb-3 sm:flex-row sm:items-center sm:justify-between">
            <div><CardTitle className="flex items-center gap-2 text-base text-bauwerk-teal"><Camera className="h-4 w-4" />Fotodokumentation</CardTitle><p className="mt-1 text-xs text-slate-500">Fotos gehören nur zu dieser Rechnung; Änderungen am Angebot wirken sich nicht aus.</p></div>
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}><ImagePlus className="mr-2 h-4 w-4" />{busy ? "Lädt hoch…" : "Fotos aufnehmen / auswählen"}</Button>
          </CardHeader>
          <CardContent>
            {photoError && <div role="alert" className="mb-3 flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"><span>{photoError}</span><button type="button" aria-label="Fehlermeldung schließen" onClick={() => setPhotoError("")}><X className="h-4 w-4" /></button></div>}
            {photoQuery.isLoading ? <div className="grid grid-cols-1 gap-3 sm:grid-cols-2"><div className="h-44 animate-pulse rounded-lg bg-slate-100" /><div className="h-44 animate-pulse rounded-lg bg-slate-100" /></div> :
              photoQuery.isError ? <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">Fotos konnten nicht geladen werden. <Button variant="link" className="h-auto p-0 text-red-800 underline" onClick={() => photoQuery.refetch()}>Erneut versuchen</Button></div> :
                photos.length === 0 ? <div className="rounded-lg border-2 border-dashed border-slate-200 px-4 py-8 text-center"><ImagePlus className="mx-auto mb-2 h-6 w-6 text-slate-400" /><p className="text-sm font-medium text-slate-700">Noch keine Fotos angehängt</p><p className="mt-1 text-xs text-slate-500">Wählen Sie mehrere Dateien aus oder nehmen Sie Fotos direkt mit dem Mobilgerät auf.</p></div> :
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{photos.map((photo) => {
                    const draft = photoDrafts[photo.id] ?? { displayName: photo.displayName, description: photo.description };
                    const changedPhoto = draft.displayName !== photo.displayName || draft.description !== photo.description;
                    return <article key={photo.id} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                      <div className="relative aspect-[16/9] bg-slate-100">
                        <img src={InvoiceApi.photoUrl(id!, photo.id)} alt={draft.displayName || photo.fileName} loading="lazy" className="h-full w-full object-cover" />
                        <span className="absolute bottom-2 left-2 rounded bg-slate-900/70 px-2 py-1 text-[11px] text-white">{formatBytes(photo.fileSize)}</span>
                      </div>
                      <div className="space-y-3 p-3">
                        <Field label="Fototitel"><Input value={draft.displayName} onChange={(e) => changed(photo.id, "displayName", e.target.value)} /></Field>
                        <Field label="Beschreibung"><Textarea rows={2} value={draft.description} onChange={(e) => changed(photo.id, "description", e.target.value)} placeholder="Was zeigt das Foto?" /></Field>
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-[11px] text-slate-500" title={photo.fileName}>{photo.fileName}</span>
                          <div className="flex shrink-0 gap-1">
                            <Button variant="outline" size="sm" disabled={!changedPhoto || photoSaving === photo.id} onClick={() => void savePhoto(photo)}>{photoSaving === photo.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Details speichern"}</Button>
                            <Button variant="ghost" size="icon" aria-label={`Foto ${draft.displayName} entfernen`} className="text-slate-500 hover:text-red-700" onClick={() => void removePhoto(photo)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      </div>
                    </article>;
                  })}</div>}
          </CardContent>
        </Card>
      </div>

      <aside className="space-y-4 xl:sticky xl:top-4">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base text-bauwerk-teal">Rechnungssumme</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between gap-3 text-slate-600"><span>Abrechenbare Positionen</span><span className="tabular-nums">{money.format(totals.subtotal)}</span></div>
              <div className="flex justify-between gap-3 text-slate-600"><span>Umsatzsteuer ({data.taxRate} %)</span><span className="tabular-nums">{money.format(totals.tax)}</span></div>
              <div className="my-3 border-t border-slate-200" />
              <div className="flex items-baseline justify-between gap-3"><span className="font-semibold text-slate-800">Gesamt</span><strong className="text-2xl font-bold tabular-nums text-bauwerk-teal">{money.format(totals.total)}</strong></div>
              <p className="pt-2 text-xs leading-relaxed text-slate-500">Die Summe berücksichtigt ausschließlich als „Abrechnen“ markierte Positionen.</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-bauwerk-teal/20 bg-bauwerk-mint/40">
          <CardContent className="p-4">
            <h2 className="mb-2 text-sm font-semibold text-bauwerk-dark-teal">Entwurf · keine steuerliche Prüfung</h2>
            <p className="text-xs leading-relaxed text-slate-600">Prüfen Sie Rechnungsdaten, Positionen und steuerliche Angaben vor dem Versand. Der PDF-Export prüft nur, ob zentrale Angaben vorhanden sind.</p>
          </CardContent>
        </Card>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" className="w-full" onClick={() => leaveTo(projectHref)}><ArrowLeft className="mr-2 h-4 w-4" />Projekt</Button>
          <Button className="w-full bg-bauwerk-orange hover:bg-bauwerk-light-orange" disabled={!dirty || saveState === "saving"} onClick={() => void save()}><Save className="mr-2 h-4 w-4" />Speichern</Button>
        </div>
        <p className="text-center text-xs text-slate-500">Zuletzt aktualisiert: {dateLabel(invoiceQuery.data.updatedAt)}</p>
      </aside>
    </fieldset>
  </main>;
}
